import React, { Dispatch, SetStateAction } from 'react';
import Chip from '@mui/material/Chip';
import { makeStyles } from '@mui/styles';
import { capitalize } from 'lodash';
import { TagType } from '../../../data/types/tags.interface';
import { Folder, SubCategoryType } from '../../../data/types/content.interface';
import { CloseIcon } from './AppIcons';

const useStyles = makeStyles({
  chip: {
    marginRight: '5px !important',
    backgroundColor: '#FAFAFA !important',
    border: '1px solid #E1E3E8 !important',
  },
  close: {
    fontSize: '20px',
    color: '#5F6774 !important',
  },
});

interface ChipsProps {
  folders?: TagType[];
  date?: string;
  themes?: TagType[];
  topics?: TagType[];
  platforms?: TagType[];
  contentTypes?: TagType[];
  brands?: TagType[];
  persons?: TagType[];
  identities?: TagType[];
  locations?: TagType[];
  events?: TagType[];
  authors?: TagType[];
  categories?: SubCategoryType[];
  subCategories?: SubCategoryType[];
  selectedFolder?: Folder[];
  setSelectedFolder?: Dispatch<SetStateAction<Folder[]>>;
  selectedCategory?: SubCategoryType[];
  setSelectedCategory?: Dispatch<SetStateAction<SubCategoryType[]>>;
  selectedSubCategory?: SubCategoryType[];
  setSelectedSubCategory?: Dispatch<SetStateAction<SubCategoryType[]>>;
  selectedType?: TagType[];
  setSelectedType?: Dispatch<SetStateAction<TagType[]>>;
  selectedThemes?: TagType[];
  setSelectedThemes?: Dispatch<SetStateAction<TagType[]>>;
  selectedTopics?: TagType[];
  setSelectedTopics?: Dispatch<SetStateAction<TagType[]>>;
  selectedSubTopics?: TagType[];
  setSelectedSubTopics?: Dispatch<SetStateAction<TagType[]>>;
  startDate?: Date | null;
  setStartDate?: Dispatch<SetStateAction<Date | null>>;
  endDate?: Date | null;
  setEndDate?: Dispatch<SetStateAction<Date | null>>;
  usedOrUnused?: string;
  setUsedOrUnused?: Dispatch<SetStateAction<string | undefined>>;
  videoType?: string;
  setVideoType?: Dispatch<SetStateAction<string | undefined>>;
  selectedPlatform?: TagType[];
  setSelectedPlatform?: Dispatch<SetStateAction<TagType[]>>;
  selectedPublishStatus?: string;
  setSelectedPublishStatus?: Dispatch<SetStateAction<string | undefined>>;
  selectedAuthors?: TagType[];
  setSelectedAuthors?: Dispatch<SetStateAction<TagType[]>>;
  selectedBrands?: TagType[];
  setSelectedBrands?: Dispatch<SetStateAction<TagType[]>>;
  selectedEvents?: TagType[];
  setSelectedEvents?: Dispatch<SetStateAction<TagType[]>>;
  selectedIdentities?: TagType[];
  setSelectedIdentities?: Dispatch<SetStateAction<TagType[]>>;
  selectedLocations?: TagType[];
  setSelectedLocations?: Dispatch<SetStateAction<TagType[]>>;
  selectedPersons?: TagType[];
  setSelectedPersons?: Dispatch<SetStateAction<TagType[]>>;
  updateAll?: (newParams: any) => void;
}

const Chips: React.FC<ChipsProps> = ({
  selectedFolder,
  setSelectedFolder,
  selectedCategory,
  setSelectedCategory,
  selectedSubCategory,
  setSelectedSubCategory,
  selectedType,
  setSelectedType,
  selectedThemes,
  setSelectedThemes,
  selectedTopics,
  setSelectedTopics,
  selectedSubTopics,
  setSelectedSubTopics,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  usedOrUnused,
  setUsedOrUnused,
  videoType,
  setVideoType,
  selectedPlatform,
  selectedPublishStatus,
  setSelectedPublishStatus,
  setSelectedPlatform,
  selectedAuthors,
  setSelectedAuthors,
  selectedBrands,
  setSelectedBrands,
  selectedEvents,
  setSelectedEvents,
  selectedIdentities,
  setSelectedIdentities,
  selectedLocations,
  setSelectedLocations,
  selectedPersons,
  setSelectedPersons,
  updateAll,
}) => {
  const classes = useStyles();

  const updateParams = (paramName: string, value: any) => {
    if (updateAll) {
      updateAll({ [paramName]: value });
    }
  };

  const handleStringFilterDelete = (
    paramName: string,
    setValue?: Dispatch<SetStateAction<string | undefined>>
  ) => () => {
    if (setValue) {
      setValue('');
    }
    updateParams(paramName, '');
  };

  const handleDateFilterDelete = (paramName: string, setValue?: Dispatch<SetStateAction<Date | null>>) => () => {
    if (setValue) {
      setValue(null);
    }
    updateParams(paramName, null);
  };

  const handleDelete = (
    selectedValues: any,
    id: number,
    paramName: string,
    setValues?: Dispatch<SetStateAction<any>>
  ) => () => {
    const newValues = selectedValues.filter((value: { id: number }) => value.id !== id);
    if (setValues) {
      setValues(newValues);
    }
    updateParams(paramName, newValues);
  };

  const getChips = () => {
    const chips: JSX.Element[] = [];

    if (usedOrUnused) {
      chips.push(
        <Chip
          key={`usedOrUnused-${usedOrUnused}`}
          label={usedOrUnused}
          className={classes.chip}
          deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
          onDelete={handleStringFilterDelete('usedOrUnused', setUsedOrUnused)}
        />
      );
    }

    if (videoType) {
      chips.push(
        <Chip
          key={`videoType-${videoType}`}
          label={capitalize(videoType)}
          className={classes.chip}
          deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
          onDelete={handleStringFilterDelete('videoType', setVideoType)}
        />
      );
    }

    if (startDate) {
      chips.push(
        <Chip
          key={`start-date-${new Date(startDate).toLocaleDateString()}`}
          label={new Date(startDate).toLocaleDateString()}
          className={classes.chip}
          deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
          onDelete={handleDateFilterDelete('startDate', setStartDate)}
        />
      );
    }

    if (endDate) {
      chips.push(
        <Chip
          key={`end-date-${new Date(endDate).toLocaleDateString()}`}
          label={new Date(endDate).toLocaleDateString()}
          className={classes.chip}
          deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
          onDelete={handleDateFilterDelete('endDate', setEndDate)}
        />
      );
    }

    if (selectedType && selectedType.length > 0) {
      selectedType?.map((contentType) => {
        chips.push(
          <Chip
            key={`contentType-${contentType?.id}`}
            label={contentType.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedType, contentType.id, 'assetType', setSelectedType)}
          />
        );
        return true;
      });
    }

    if (selectedFolder && selectedFolder.length > 0) {
      selectedFolder?.map((folder) => {
        chips.push(
          <Chip
            key={`folder-${folder?.id}`}
            label={folder.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedFolder, folder.id, 'folder', setSelectedFolder)}
          />
        );
        return true;
      });
    }

    if (selectedCategory && selectedCategory.length > 0) {
      selectedCategory?.map((c) => {
        chips.push(
          <Chip
            key={`category-${c?.id}`}
            label={c.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedCategory, c.id, 'category', setSelectedCategory)}
          />
        );
        return true;
      });
    }

    if (selectedSubCategory && selectedSubCategory.length > 0) {
      selectedSubCategory?.map((sc) => {
        chips.push(
          <Chip
            key={`sub-category-${sc?.id}`}
            label={sc.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedSubCategory, sc.id, 'subCategory', setSelectedSubCategory)}
          />
        );
        return true;
      });
    }

    if (selectedThemes && selectedThemes.length > 0) {
      selectedThemes?.map((theme) => {
        chips.push(
          <Chip
            key={`theme-${theme?.id}`}
            label={theme.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedThemes, theme.id, 'themes', setSelectedThemes)}
          />
        );
        return true;
      });
    }

    if (selectedTopics && selectedTopics.length > 0) {
      selectedTopics?.map((topic) => {
        chips.push(
          <Chip
            key={`topic-${topic?.id}`}
            label={topic.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedTopics, topic.id, 'topics', setSelectedTopics)}
          />
        );
        return true;
      });
    }

    if (selectedSubTopics && selectedSubTopics.length > 0) {
      selectedSubTopics?.map((subTopic) => {
        chips.push(
          <Chip
            key={`sub-topic-${subTopic?.id}`}
            label={subTopic.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedSubTopics, subTopic.id, 'subTopics', setSelectedSubTopics)}
          />
        );
        return true;
      });
    }

    if (selectedPlatform && selectedPlatform.length > 0) {
      selectedPlatform?.map((p) => {
        chips.push(
          <Chip
            key={`platform-${p?.id}`}
            label={p.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedPlatform, p.id, 'platform', setSelectedPlatform)}
          />
        );
        return true;
      });
    }

    if (selectedPublishStatus) {
      chips.push(
        <Chip
          key={`publish-status-${selectedPublishStatus}`}
          label={selectedPublishStatus}
          className={classes.chip}
          deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
          onDelete={handleStringFilterDelete('publishStatus', setSelectedPublishStatus)}
        />
      );
    }

    if (selectedAuthors && selectedAuthors.length > 0) {
      selectedAuthors?.map((author) => {
        chips.push(
          <Chip
            key={`author-${author?.id}`}
            label={author.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedAuthors, author.id, 'authors', setSelectedAuthors)}
          />
        );
        return true;
      });
    }

    if (selectedBrands && selectedBrands.length > 0) {
      selectedBrands?.map((brand) => {
        chips.push(
          <Chip
            key={`brand-${brand?.id}`}
            label={brand.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedBrands, brand.id, 'brands', setSelectedBrands)}
          />
        );
        return true;
      });
    }

    if (selectedEvents && selectedEvents.length > 0) {
      selectedEvents?.map((event) => {
        chips.push(
          <Chip
            key={`event-${event?.id}`}
            label={event.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedEvents, event.id, 'events', setSelectedEvents)}
          />
        );
        return true;
      });
    }

    if (selectedIdentities && selectedIdentities.length > 0) {
      selectedIdentities?.map((identity) => {
        chips.push(
          <Chip
            key={`identity-${identity?.id}`}
            label={identity.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedIdentities, identity.id, 'identities', setSelectedIdentities)}
          />
        );
        return true;
      });
    }

    if (selectedLocations && selectedLocations.length > 0) {
      selectedLocations?.map((location) => {
        chips.push(
          <Chip
            key={`location-${location?.id}`}
            label={location.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedLocations, location.id, 'locations', setSelectedLocations)}
          />
        );
        return true;
      });
    }

    if (selectedPersons && selectedPersons.length > 0) {
      selectedPersons?.map((person) => {
        chips.push(
          <Chip
            key={`person-${person?.id}`}
            label={person.name}
            className={classes.chip}
            deleteIcon={<CloseIcon viewBox="10 8 24 25" className={classes.close} sx={{ fontSize: '34px' }} />}
            onDelete={handleDelete(selectedPersons, person.id, 'persons', setSelectedFolder)}
          />
        );
        return true;
      });
    }
    return chips;
  };

  return <>{getChips()}</>;
};

export default Chips;
